/**
 * Lightspeed Main Category Alignment Exception Config
 *
 * Covers: ex-product-ls-category-alignment
 *
 * Routes through the shared compare-table renderer (open-list variant with
 * a category-specific status taxonomy - Near match / Extra / Missing /
 * Present). The backend emits the unified ``expected`` / ``found`` /
 * ``field_matches`` / ``issue_text`` payload alongside the legacy fields,
 * so the only thing the FP-side needs to add is a Products column for
 * sort + impact context.
 */
import React from 'react'
import { Typography } from '@mui/material'
import { typographyEl } from '../shared/muiElements'
import {
  buildMasterCompareConfig,
  type CompareField,
} from '../shared/masterCompareTable'
import type { ExceptionColumnDef, ExceptionConfig } from '../types'


const CATEGORY_FIELDS: CompareField[] = [
  { key: 'name', label: 'Category' },
]


const emptyDash = React.createElement(
  Typography,
  { variant: 'body2', sx: { color: 'text.disabled' } },
  '—',
)


const renderProductCount = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const count = row.product_count
  if (count === null || count === undefined) return emptyDash
  return typographyEl(
    { component: 'span', variant: 'body2' },
    Number(count).toLocaleString(),
  )
}


const productColumns: ExceptionColumnDef[] = [
  {
    key: 'product_count',
    label: '# Products',
    align: 'right',
    contexts: ['inspector'],
    render: renderProductCount,
    getSortValue: (row) => row.product_count ?? -1,
  },
]


const platformExtras: ExceptionColumnDef[] = [
  {
    key: 'similarity_score',
    label: 'Similarity',
    align: 'right',
    contexts: ['platform'],
  },
]


export const categoryAlignmentConfig: ExceptionConfig = buildMasterCompareConfig({
  codes: ['ex-product-ls-category-alignment'],
  statusSet: 'open',
  compareFields: CATEGORY_FIELDS,
  systemLabel: 'Lightspeed',
  // Category name IS the master - identical to master_value, so
  // hide the duplicate Expected Category column in the platform context.
  masterKeyField: 'name',
  statusKeys: ['extra', 'near_match', 'missing', 'present'],
  extraInspectorColumns: productColumns,
  extraPlatformColumns: platformExtras,
})
